import React from 'react';
import { Activity, BarChart3, ChevronDown, ChevronUp, Loader2, Users, Zap } from 'lucide-react';
import { StatCard } from '../SharedComponents';

const PulseTab = ({ ctx }) => {
  const { members, pulseData } = ctx;

  return (
<div data-testid="pulse-tab">
  <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>Team Wellness Pulse</h1>
  <p className="text-xs mb-5" style={{ color: '#999' }}>
    Anonymous aggregated pulse surveys — individual responses are never visible. Track team wellbeing trends weekly.
  </p>

  {pulseData?.demo_data && (
    <div className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium mb-4" style={{ backgroundColor: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE' }}>
      <BarChart3 size={14} />
      Sample data — showing what pulse surveys will look like once team members respond
    </div>
  )}

  {pulseData && (
    <>
      {/* Overall score */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <div className="bg-white rounded-xl p-5 text-center" style={{ border: '1px solid #E8E8E8' }} data-testid="pulse-overall">
          <p className="text-4xl font-black" style={{ color: pulseData.overall_score >= 4 ? '#059669' : pulseData.overall_score >= 3 ? '#F59E0B' : '#DC2626' }}>
            {pulseData.overall_score}
          </p>
          <p className="text-[10px] font-bold mt-1" style={{ color: '#999' }}>Overall Score (1-5)</p>
        </div>
        <StatCard label="Participation Rate" value={`${pulseData.participation_rate}%`} icon={<Users size={16} style={{ color: '#2563EB' }} />} />
        <StatCard label="Responses This Week" value={pulseData.total_responses_this_week} icon={<Zap size={16} style={{ color: '#F5831F' }} />} />
      </div>

      {/* Per-question breakdown */}
      <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E8E8E8' }} data-testid="pulse-questions">
        <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: '#1A1209' }}>
          <Activity size={16} style={{ color: '#8B5CF6' }} />
          Question-by-Question Breakdown
        </h3>
        <div className="space-y-4">
          {pulseData.results?.map((q, i) => {
            const pct = (q.week_avg / 5) * 100;
            const color = q.week_avg >= 4 ? '#10B981' : q.week_avg >= 3 ? '#F59E0B' : '#EF4444';
            return (
              <div key={i} data-testid={`pulse-q-${i}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-medium" style={{ color: '#1A1209' }}>{q.question}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-black" style={{ color }}>{q.week_avg}</span>
                    {q.trend !== 0 && (
                      <span className="text-[10px] font-bold flex items-center gap-0.5" style={{ color: q.trend > 0 ? '#059669' : '#DC2626' }}>
                        {q.trend > 0 ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                        {q.trend > 0 ? '+' : ''}{q.trend}
                      </span>
                    )}
                  </div>
                </div>
                <div className="w-full h-3 rounded-full" style={{ backgroundColor: '#F0F0F0' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[9px]" style={{ color: '#CCC' }}>30-day avg: {q.month_avg}</span>
                  <span className="text-[9px]" style={{ color: '#CCC' }}>{q.response_count} responses</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pulse scale legend */}
      <div className="mt-4 flex items-center justify-center gap-6">
        {[{ label: 'Low (1-2.9)', color: '#EF4444' }, { label: 'Moderate (3-3.9)', color: '#F59E0B' }, { label: 'High (4-5)', color: '#10B981' }].map(l => (
          <div key={l.label} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: l.color }} />
            <span className="text-[10px]" style={{ color: '#999' }}>{l.label}</span>
          </div>
        ))}
      </div>
    </>
  )}

  {!pulseData && (
    <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
      <Loader2 size={24} className="animate-spin mx-auto mb-3" style={{ color: '#DDD' }} />
      <p className="text-sm" style={{ color: '#999' }}>Loading pulse survey data...</p>
    </div>
  )}
</div>
  );
};

export default PulseTab;
